import {
    Acl,
    AclFormatted,
    FormattedAclWithLicenseInfo,
    PaginatedAclsWithProfilesFormatted
} from 'common/types/Entities';
import { getConditionsToDisplay } from 'common/utils/AclConditionsHelpers';
import { _getAclEnriesStatusTooltipDates, _removeAclEntriesStatusTooltipEmptyDates, calculateAclEntriesStatus } from 'common/utils/AclEntriesStatusTooltipHelpers';
import { dateToFrontEndFormat } from 'common/utils/dateHelpers';
import { AclEntriesPage } from 'features/aclEntries/aclEntriesTypeDefs';

import {
    APIAclEntryDeleteParams,
    APIAclEntryPostParams,
    APIAclEntryPutParams,
    GetAclWithProfileEntriesResponse,
    GetAclWithProfileEntry,
    UserAclEntriesDetails,
    UserGroupAclEntriesDetails
} from './AclEntitiesApiTypeDefs';

export const _formatAcl = (rows: Acl[]) : AclFormatted[] => {
    return rows.map(item => ({
        ...item,
        id: `${item.accessId}.${item.resourceId}.${item.licenseId}.${item.usergroupId}.${item.tenantId}.${item.userId}`,
        resourceTableName: item.resourceDisplayName ?? item.resourceName ?? item.resourceId,
        accessTableName: item.accessName ?? item.accessId,
        startDate: item.startDate ? dateToFrontEndFormat(item.startDate) : '',
        endDate: item.endDate ? dateToFrontEndFormat(item.endDate) : '',
        principalName: item.userId
            ? item.userId
            : item.usergroupName,
        conditionsToDisplay: getConditionsToDisplay(item.conditions)
    }));
};

export const _formatAclWithProfile = (
    response: GetAclWithProfileEntriesResponse
): PaginatedAclsWithProfilesFormatted => {
    const responseData = response.data;

    return {
        data: responseData.data.map((row: GetAclWithProfileEntry) => ({
            ...row,
            ...row.profile,
            id: `${row.accessId}.${row.resourceId}.${row.licenseId}.${row.usergroupId}.${row.tenantId}.${row.userId}`,
            startDate: row.startDate ? dateToFrontEndFormat(row.startDate) : '',
            endDate: row.endDate ? dateToFrontEndFormat(row.endDate) : '',
            conditionsToDisplay: getConditionsToDisplay(row.conditions)
        })),
        skipped: responseData.skipped,
        taken: responseData.taken,
        count: responseData.count
    };
};

export const _formatUserAclsWithLicenseInfo = (
    aclEntriesWithLicenseDetails: UserAclEntriesDetails
): FormattedAclWithLicenseInfo[] => {
    const { userAclEntries, licenses, userTenantMembership } = aclEntriesWithLicenseDetails;
    const currentAclPage =  AclEntriesPage.TenantUser;

    return userAclEntries.map((aclEntry: AclFormatted) => {
        const licenseByAclEntry = licenses.find(license => license.id === aclEntry.licenseId);

        const licenseBillingRecord = licenseByAclEntry?.billingRecord ?? '';
        const aclEntriesStatusTooltipDates = _getAclEnriesStatusTooltipDates(aclEntry, userTenantMembership, licenseByAclEntry);
        const { tooltipProps, status } = calculateAclEntriesStatus(aclEntriesStatusTooltipDates, currentAclPage);

        return {
            ...aclEntry,
            licenseBillingRecord,
            status,
            statusTooltip: tooltipProps,
            currentPage: currentAclPage
        };
    });
};

export const _formatUserGroupAclsWithLicenseInfo = (
    aclEntriesWithLicenseDetails: UserGroupAclEntriesDetails
): FormattedAclWithLicenseInfo[] => {
    const { userGroupAclEntries, licenses } = aclEntriesWithLicenseDetails;
    const currentAclPage =  AclEntriesPage.TenantUserGroup;

    return userGroupAclEntries.map((aclEntry: AclFormatted) => {
        const licenseByAclEntry = licenses.find(license => license.id === aclEntry.licenseId);

        const licenseBillingRecord = licenseByAclEntry?.billingRecord ?? '';
        const aclEntriesStatusTooltipDates = _getAclEnriesStatusTooltipDates(aclEntry, undefined, licenseByAclEntry);
        const filteredAclEntriesStatusTooltipDates = _removeAclEntriesStatusTooltipEmptyDates(aclEntriesStatusTooltipDates);
        const { tooltipProps, status } = calculateAclEntriesStatus(filteredAclEntriesStatusTooltipDates, currentAclPage);

        return {
            ...aclEntry,
            licenseBillingRecord,
            status,
            statusTooltip: tooltipProps,
            currentPage: currentAclPage
        };
    });
};

export const _isLicenseAclEntriesPage = (aclPage: string): boolean => aclPage === AclEntriesPage.License;

export const _getAclEntriesPostPutParams = (item: APIAclEntryPostParams | APIAclEntryPutParams)
: APIAclEntryPostParams | APIAclEntryPutParams => {
    return {
        tenantId: item.tenantId,
        resourceId: item.resourceId,
        accessId: item.accessId,
        userId: item.userId,
        usergroupId: item.usergroupId,
        licenseId: item.licenseId,
        startDate: item.startDate,
        endDate: item.endDate,
        tenantRequiresLicense: item.tenantRequiresLicense,
        conditions: item.conditions
    };
};

export const _getAclEntriesDeleteParams = (item: APIAclEntryDeleteParams)
: APIAclEntryDeleteParams => {
    return {
        tenantId: item.tenantId,
        resourceId: item.resourceId,
        accessId: item.accessId,
        userId: item.userId,
        usergroupId: item.usergroupId,
        licenseId: item.licenseId
    };
};
